import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  BadgeCheck,
  ChevronDown,
  ChevronRight,
  CircleDot,
  ExternalLink,
  Link2Off,
  RefreshCw,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import type {
  FptChangeClassification,
  FptCostReliability,
  FptEntitySyncMetadata,
  FptFinancialSummary,
  FptPipelineStepStatus,
  FptProfitabilityStatus,
  FptProfitabilitySummary,
  FptSyncStatus,
} from "../models/production";
import { computeInvoiceReadiness } from "../services/invoiceReadiness";
import { calculateAssetValue, calculateShotValue } from "../lib/bidVersioning";
import type { Asset, AssetRateCard, ProjectSetup, RateCard, Shot } from "../types";
import { getBidStatusValue } from "../lib/bidStatus";
import { parseShotCode } from "../shared/shotCodeParser";
import { cn } from "../utils";

const STRUCTURE_REVIEW_VISIBILITY_STORAGE_KEY = "shotgrid-structure-review-visible";
const SHOTGRID_SNAPSHOT_CACHE_STORAGE_KEY = "shotgrid-project-snapshot-cache";

type SyncBadgeTone = "slate" | "amber" | "blue" | "emerald" | "rose" | "violet";
type DashboardStatus =
  | "not_linked"
  | "not_pushed"
  | "pending"
  | "in_sync"
  | "out_of_sync"
  | "error"
  | "completed"
  | "invoice_ready"
  | "new"
  | "changed"
  | "needs_review"
  | "removed";

interface ShotGridDashboardProps {
  projectId: string | null;
  setup: ProjectSetup;
  rates: RateCard;
  assetRates: AssetRateCard;
  shots: Shot[];
  assets: Asset[];
  fptProjectId?: string | null;
  fptProjectName?: string | null;
  lastPulledAt?: string;
  financials?: FptFinancialSummary;
  profitability?: FptProfitabilitySummary;
  fptStructureInspection?: FptStructureInspection | null;
  onProjectMetadataRefresh?: (metadata: {
    fptProjectId?: string | null;
    fptProjectName?: string | null;
    fptProjectUrl?: string | null;
    fptLinked?: boolean;
    lastPulledAt?: string;
    financials?: FptFinancialSummary;
    profitability?: FptProfitabilitySummary;
    fptStructureInspection?: FptStructureInspection | null;
  }) => void;
}

interface FptRepairPlanItem {
  fptTaskId: string;
  taskName: string;
  parentEntityType: "shot" | "asset";
  parentEntityId: string;
  parentEntityName: string;
  localTaskId?: string;
  mappingStatus: "valid" | "missing" | "ambiguous";
  repairable: boolean;
  reason?: string;
  resolvedPipelineStepId?: string;
  resolvedPipelineStepName?: string;
  resolvedDepartmentName?: string;
}

interface FptRepairPlan {
  projectId: string;
  pulledAt: string;
  repairableCount: number;
  reviewCount: number;
  impactedEntityCount: number;
  repairable: FptRepairPlanItem[];
  needsReview: FptRepairPlanItem[];
}

interface FptStructureIssue {
  id: string;
  entityType: "shot" | "asset" | "task";
  side: "app_only" | "fpt_only" | "ambiguous";
  classification: "missing_in_app" | "missing_in_fpt" | "awaiting_award" | "delivered_legacy_drift" | "manual_decision";
  name: string;
  parentName?: string;
  note: string;
  localEntityId?: string;
}

interface FptStructureInspection {
  projectId: string;
  fptProjectId: string;
  inspectedAt: string;
  summary: {
    matchedShots: number;
    matchedAssets: number;
    matchedTasks: number;
    appOnlyShots: number;
    appOnlyAssets: number;
    appOnlyTasks: number;
    fptOnlyShots: number;
    fptOnlyAssets: number;
    fptOnlyTasks: number;
    ambiguousShots: number;
    ambiguousAssets: number;
    ambiguousTasks: number;
  };
  appOnly: FptStructureIssue[];
  fptOnly: FptStructureIssue[];
  ambiguous: FptStructureIssue[];
}

type StructureReviewGroup = {
  title: string;
  description: string;
  items: FptStructureIssue[];
  tone: string;
  counts: {
    missingInApp: number;
    missingInFpt: number;
    awaitingAward: number;
    deliveredLegacyDrift: number;
    needsManualReview: number;
  };
};

type ItemRow = {
  id: string;
  itemType: "shot" | "asset";
  name: string;
  groupKey: string;
  groupLabel: string;
  groupOrder: number;
  bidStatus: string;
  fptStatusLabel: string;
  syncStatus: DashboardStatus;
  pipelineStatus: DashboardStatus;
  invoiceStatus: DashboardStatus;
  lastContext: string;
  warning?: string;
  comparisonStatus?: string;
  invoiceStatusNote?: string;
  invoiceReady: boolean;
  completedInFpt: boolean;
  alreadyInvoiced: boolean;
  totalLoggedHours?: number;
  totalCost?: number;
  costReliabilityLevel?: FptCostReliability;
  costReliability?: string;
  financialContext?: string;
  bidValue?: number;
  grossProfit?: number;
  grossMarginPct?: number;
  profitabilityStatus?: FptProfitabilityStatus;
  profitabilityReliabilityLevel?: FptCostReliability;
  profitabilityReliability?: string;
  profitabilityContext?: string;
  profitabilityNote?: string;
};

type ReviewGroupKey = "to_create" | "to_update" | "needs_review" | "removed";

type ReviewGroup = {
  key: ReviewGroupKey;
  title: string;
  items: Array<{ id: string; name: string; itemType: "shot" | "asset"; statusLabel: string; note?: string }>;
};

type ItemFilter = "all" | "invoice_ready" | "blocked" | "profitable" | "loss_making" | "low_confidence";
type StructureReviewFilter = "all" | "shots" | "tasks" | "only_in_app" | "only_in_fpt" | "duplicates" | "needs_manual_review";

type LocalProjectSnapshot = {
  setup: ProjectSetup;
  rates?: RateCard;
  assetRates?: AssetRateCard;
  shots: Shot[];
  assets: Asset[];
  fptProjectId?: string | null;
  fptProjectName?: string | null;
  fptProjectUrl?: string;
  fptLinked?: boolean;
  lastPulledAt?: string;
  lastPushedAt?: string;
  syncStatus?: FptSyncStatus;
  entitySyncMetadata?: Record<string, FptEntitySyncMetadata>;
  financials?: FptFinancialSummary;
  profitability?: FptProfitabilitySummary;
  fptStructureInspection?: FptStructureInspection | null;
};

const STATUS_STYLES: Record<DashboardStatus, { label: string; tone: SyncBadgeTone }> = {
  not_linked: { label: "Not Linked", tone: "slate" },
  not_pushed: { label: "Not Pushed", tone: "slate" },
  pending: { label: "Pending", tone: "amber" },
  in_sync: { label: "In Sync", tone: "emerald" },
  out_of_sync: { label: "Out of Sync", tone: "blue" },
  error: { label: "Error", tone: "rose" },
  completed: { label: "Completed", tone: "violet" },
  invoice_ready: { label: "Invoice Ready", tone: "emerald" },
  new: { label: "New", tone: "blue" },
  changed: { label: "Changed", tone: "amber" },
  needs_review: { label: "Needs Review", tone: "rose" },
  removed: { label: "Removed", tone: "slate" },
};

const TONE_STYLES: Record<SyncBadgeTone, string> = {
  slate: "border-slate-200 bg-slate-100 text-slate-700",
  amber: "border-amber-200 bg-amber-50 text-amber-800",
  blue: "border-sky-200 bg-sky-50 text-sky-800",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
  rose: "border-rose-200 bg-rose-50 text-rose-800",
  violet: "border-violet-200 bg-violet-50 text-violet-800",
};

const CHANGE_CLASSIFICATION_LABELS: Partial<Record<FptChangeClassification, string>> = {
  new: "New",
  changed: "Changed",
  missing_in_fpt: "Missing in FPT",
  missing_in_app: "Missing in App",
  rebid: "Rebid",
  removed_from_current_bid: "Removed from Current Bid",
  archived_candidate: "Archive Candidate",
};

const FPT_STATUS_LABELS: Record<string, string> = {
  ip: "In Progress",
  rdy: "Ready to Start",
  fin: "Final",
  paf: "PAF",
  omt: "Omit",
  hld: "On Hold",
  rev: "Pending Review",
  dlvr: "Delivered",
  ap: "Awaiting Process",
  ps: "Potential Shot",
  pr: "Pending Reprocess",
  qcpen: "QC Pending",
};

function toDashboardSyncStatus(metadata?: FptEntitySyncMetadata): DashboardStatus {
  const classification = metadata?.changeClassification;
  if (metadata?.syncStatus === "error") return "error";
  if (classification === "new" || metadata?.syncStatus === "not_pushed") return "new";
  if (classification === "changed" || classification === "rebid" || metadata?.syncStatus === "out_of_sync") return "changed";
  if (classification === "removed_from_current_bid" || metadata?.archiveCandidate) return "removed";
  if (metadata?.conflictFlag) return "needs_review";
  if (metadata?.syncStatus === "pending") return "pending";
  if (metadata?.syncStatus === "in_sync") return "in_sync";
  if (metadata?.syncStatus === "not_linked") return "not_linked";
  return "not_pushed";
}

function toPipelineDashboardStatus(metadata?: FptEntitySyncMetadata): DashboardStatus {
  const status = metadata?.pipelineStepStatus as FptPipelineStepStatus | undefined;
  if (status === "missing" || Number(metadata?.tasksMissingPipelineStep || 0) > 0) return "needs_review";
  if (status === "valid") return "completed";
  if ((metadata?.taskCount || 0) > 0) return "pending";
  return "not_pushed";
}

function toInvoiceStatus(item: Shot | Asset, metadata?: FptEntitySyncMetadata): DashboardStatus {
  const invoice = computeInvoiceReadiness(item, metadata);
  if (invoice.invoiceReady) return "invoice_ready";
  if (invoice.alreadyInvoiced) return "completed";
  if (metadata?.syncStatus === "error" || metadata?.pipelineStepStatus === "missing" || Number(metadata?.tasksMissingPipelineStep || 0) > 0 || metadata?.conflictFlag) {
    return "needs_review";
  }
  if (invoice.completedInFpt) return "completed";
  return "pending";
}

function StatusBadge({ status }: { status: DashboardStatus }) {
  const config = STATUS_STYLES[status];
  return (
    <span className={cn("inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-semibold", TONE_STYLES[config.tone])}>
      <CircleDot className="h-3.5 w-3.5" />
      {config.label}
    </span>
  );
}

function SummaryCard({
  title,
  value,
  detail,
  tone = "slate",
}: {
  title: string;
  value: string;
  detail: string;
  tone?: SyncBadgeTone;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{title}</div>
        <span className={cn("inline-flex shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold", TONE_STYLES[tone])}>{value}</span>
      </div>
      <p className="mt-3 text-xs leading-5 text-slate-600">{detail}</p>
    </div>
  );
}

function WarningSurface({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3.5 shadow-sm">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-700" />
        <div>
          <div className="font-semibold text-amber-900">{title}</div>
          <p className="mt-1 text-xs leading-5 text-amber-800">{detail}</p>
        </div>
      </div>
    </div>
  );
}

function getStructureIssueSideLabel(issue: FptStructureIssue) {
  if (issue.classification === "awaiting_award") return "Awaiting Award";
  if (issue.classification === "delivered_legacy_drift") return "Delivered Legacy Drift";
  if (issue.classification === "missing_in_fpt") return "Missing in FPT";
  if (issue.classification === "missing_in_app") return "Missing in App";
  if (issue.side === "app_only") return "Missing in FPT";
  if (issue.side === "fpt_only") return "Missing in App";
  return "Manual Decision";
}

function getStructureIssueSideTone(issue: FptStructureIssue) {
  if (issue.classification === "awaiting_award") return "border-violet-200 bg-violet-50 text-violet-800";
  if (issue.classification === "delivered_legacy_drift") return "border-orange-200 bg-orange-50 text-orange-800";
  if (issue.side === "app_only") return "border-amber-200 bg-amber-50 text-amber-800";
  if (issue.side === "fpt_only") return "border-sky-200 bg-sky-50 text-sky-800";
  return "border-rose-200 bg-rose-50 text-rose-800";
}

function formatStructureIssueCounts(items: FptStructureIssue[]) {
  const missingInFpt = items.filter((item) => item.classification === "missing_in_fpt").length;
  const missingInApp = items.filter((item) => item.classification === "missing_in_app").length;
  const awaitingAward = items.filter((item) => item.classification === "awaiting_award").length;
  const deliveredLegacyDrift = items.filter((item) => item.classification === "delivered_legacy_drift").length;
  const needsManualReview = items.filter((item) => item.classification === "manual_decision").length;
  return {
    missingInApp,
    missingInFpt,
    awaitingAward,
    deliveredLegacyDrift,
    needsManualReview,
  };
}

function formatPullContext(metadata?: FptEntitySyncMetadata) {
  const fragments: string[] = [];
  if (metadata?.lastPulledAt) fragments.push(`Pulled ${new Date(metadata.lastPulledAt).toLocaleString()}`);
  if ((metadata?.taskCount || 0) > 0) fragments.push(`${metadata?.taskCount} task${metadata?.taskCount === 1 ? "" : "s"}`);
  if (Number(metadata?.tasksMissingPipelineStep || 0) > 0) fragments.push(`${metadata?.tasksMissingPipelineStep} missing step${Number(metadata?.tasksMissingPipelineStep) === 1 ? "" : "s"}`);
  return fragments.join(" • ") || "Awaiting pull from FPT";
}

function formatHours(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return "";
  return `${Math.round(value * 10) / 10}h logged`;
}

function formatLoggedDays(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return "";
  const dayValue = Math.round((value / 8) * 10) / 10;
  return `${dayValue} days logged`;
}

function formatCurrency(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatCostReliability(summary?: FptFinancialSummary) {
  const reliability = summary?.costReliability;
  if (!reliability) return "";
  if (reliability === "high") return "rate coverage high";
  if (reliability === "medium") return "cost provisional";
  if ((summary?.timeLogCount || 0) > 0 && typeof summary?.totalCost === "number") return "rate coverage partial";
  if ((summary?.timeLogCount || 0) > 0) return "timelogs landed, rates incomplete";
  return "rates incomplete";
}

function formatFinancialContext(summary?: FptFinancialSummary, fallbackHours?: number) {
  const fragments: string[] = [];
  const loggedHours = summary?.totalLoggedHours ?? fallbackHours;
  const hoursLabel = formatHours(loggedHours);
  if (hoursLabel) fragments.push(hoursLabel);
  if (typeof summary?.totalCost === "number") {
    fragments.push(`${formatCurrency(summary.totalCost)}${summary.provisional ? " (provisional)" : ""}`);
  } else if ((summary?.timeLogCount || 0) > 0) {
    fragments.push("cost unavailable");
  }
  const reliability = formatCostReliability(summary);
  if (reliability) fragments.push(reliability);
  return fragments.join(" • ");
}

function getProjectFinancialSnapshotValue(summary?: FptFinancialSummary, fallbackTimestamp?: string) {
  const loggedDaysLabel = formatLoggedDays(summary?.totalLoggedHours);
  if (loggedDaysLabel) return loggedDaysLabel;
  if ((summary?.timeLogCount || 0) > 0) return "Timelogs landed";
  if (summary?.lastTimelogPullAt || fallbackTimestamp) return "Finance snapshot pending";
  return "Awaiting timelog pull";
}

function getProjectFinancialSnapshotDetail(
  summary: FptFinancialSummary | undefined,
  invoiceReadyCount: number,
  fallbackTimestamp?: string
) {
  const fragments: string[] = [];

  if (typeof summary?.totalCost === "number") {
    fragments.push(`${formatCurrency(summary.totalCost)}${summary.provisional ? " (provisional)" : ""}`);
  } else if ((summary?.timeLogCount || 0) > 0) {
    fragments.push("Cost unavailable");
  } else if (summary?.lastTimelogPullAt || fallbackTimestamp) {
    fragments.push("Awaiting refreshed timelog totals");
  } else {
    fragments.push("No timelog pull yet");
  }

  const reliability = formatCostReliability(summary);
  if (reliability) fragments.push(reliability);
  fragments.push(`${invoiceReadyCount} ready to invoice`);

  return fragments.join(" • ");
}

function formatMargin(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "";
  return `${Math.round(value * 10) / 10}% margin`;
}

function formatProfitabilityReliability(summary?: FptProfitabilitySummary, financials?: FptFinancialSummary) {
  const reliability = summary?.profitabilityReliability;
  if (!reliability) return "";
  if (reliability === "high") return "high confidence";
  if (reliability === "medium") return "provisional";
  if ((financials?.timeLogCount || 0) > 0 && typeof financials?.totalCost === "number") return "needs reconciliation";
  if ((financials?.timeLogCount || 0) > 0) return "timelog evidence only";
  if ((summary?.note || "").toLowerCase().includes("fallback")) return "forecast-led";
  return "low confidence";
}

function formatProfitabilityTimestamp(summary?: FptProfitabilitySummary, fallbackTimestamp?: string) {
  const timestamp = summary?.lastComputedAt || fallbackTimestamp;
  if (!timestamp) return "";
  return `As of ${new Date(timestamp).toLocaleString()}`;
}

function pickNewerProfitability(
  current?: FptProfitabilitySummary,
  incoming?: FptProfitabilitySummary
): FptProfitabilitySummary | undefined {
  if (!current) return incoming;
  if (!incoming) return current;

  const currentTime = current.lastComputedAt ? Date.parse(current.lastComputedAt) : Number.NaN;
  const incomingTime = incoming.lastComputedAt ? Date.parse(incoming.lastComputedAt) : Number.NaN;

  if (Number.isFinite(currentTime) && Number.isFinite(incomingTime)) {
    return incomingTime >= currentTime ? incoming : current;
  }

  if (Number.isFinite(incomingTime)) return incoming;
  if (Number.isFinite(currentTime)) return current;

  return incoming;
}

function pickNewerFinancials(
  current?: FptFinancialSummary,
  incoming?: FptFinancialSummary
): FptFinancialSummary | undefined {
  if (!current) return incoming;
  if (!incoming) return current;

  const currentTime = current.lastTimelogPullAt ? Date.parse(current.lastTimelogPullAt) : Number.NaN;
  const incomingTime = incoming.lastTimelogPullAt ? Date.parse(incoming.lastTimelogPullAt) : Number.NaN;

  if (Number.isFinite(currentTime) && Number.isFinite(incomingTime)) {
    return incomingTime >= currentTime ? incoming : current;
  }

  if (Number.isFinite(incomingTime)) return incoming;
  if (Number.isFinite(currentTime)) return current;

  return incoming;
}

function resolveBidValue(
  item: Shot | Asset,
  itemType: "shot" | "asset",
  setup: ProjectSetup,
  rates: RateCard,
  assetRates: AssetRateCard
) {
  if (typeof item.originalAwardedValue === "number" && Number.isFinite(item.originalAwardedValue)) {
    return item.originalAwardedValue;
  }
  if (typeof item.legacyPrice === "number" && Number.isFinite(item.legacyPrice)) {
    return item.legacyPrice;
  }
  return itemType === "shot"
    ? calculateShotValue(item as Shot, rates, setup)
    : calculateAssetValue(item as Asset, assetRates, setup);
}

function formatProfitabilityContext(summary?: FptProfitabilitySummary, fallbackBidValue?: number) {
  const fragments: string[] = [];
  const bidValue = summary?.bidValue ?? fallbackBidValue;
  if (typeof bidValue === "number") fragments.push(`Bid ${formatCurrency(bidValue)}`);
  if (typeof summary?.totalCost === "number") {
    fragments.push(`Cost ${formatCurrency(summary.totalCost)}${summary.provisional ? " (provisional)" : ""}`);
  }
  if (typeof summary?.grossProfit === "number") fragments.push(`Profit ${formatCurrency(summary.grossProfit)}`);
  const marginLabel = formatMargin(summary?.grossMarginPct);
  if (marginLabel) fragments.push(marginLabel);
  if (summary?.note) fragments.push(summary.note);
  const reliability = formatProfitabilityReliability(summary);
  if (reliability) fragments.push(reliability);
  return fragments.join(" • ");
}

function renderProfitabilityContextWithHighlightedBid(context?: string) {
  if (!context) return null;

  const bidMatch = context.match(/^Bid (£[^•]+)(?: • )?(.*)$/);
  if (!bidMatch) {
    return <span>{context}</span>;
  }

  const [, bidValue, remainder] = bidMatch;
  return (
    <>
      <span>
        Bid <span className="text-[#ffcc01]">{bidValue}</span>
      </span>
      {remainder ? <span>{` • ${remainder}`}</span> : null}
    </>
  );
}

function formatFptStatusLabel(rawStatus?: string | null) {
  const trimmed = String(rawStatus || "").trim();
  if (!trimmed) return "Awaiting FPT status";
  const normalized = trimmed.toLowerCase();
  const friendly = FPT_STATUS_LABELS[normalized];
  if (!friendly) return trimmed;
  return friendly;
}

function ProfitabilityBadge({
  status,
}: {
  status?: FptProfitabilityStatus;
}) {
  if (!status) return null;

  const tone =
    status === "profitable"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : status === "break_even"
        ? "border-amber-200 bg-amber-50 text-amber-800"
        : status === "loss_making"
          ? "border-rose-200 bg-rose-50 text-rose-800"
          : "border-slate-200 bg-slate-100 text-slate-700";

  const label =
    status === "profitable"
      ? "Profitable"
      : status === "break_even"
        ? "Near Break-even"
        : status === "loss_making"
          ? "Loss-making"
          : "Insufficient Data";

  return <span className={cn("inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold", tone)}>{label}</span>;
}

function buildReviewGroups(itemRows: ItemRow[], entityMetadata: Record<string, FptEntitySyncMetadata>): ReviewGroup[] {
  const groups: ReviewGroup[] = [
    { key: "to_create", title: "To Create", items: [] },
    { key: "to_update", title: "To Update", items: [] },
    { key: "needs_review", title: "Needs Review", items: [] },
    { key: "removed", title: "Removed / Archive Candidates", items: [] },
  ];

  for (const item of itemRows) {
    const metadata = entityMetadata[item.id];
    const statusLabel = metadata?.changeClassification ? CHANGE_CLASSIFICATION_LABELS[metadata.changeClassification] || item.comparisonStatus || "Review" : item.comparisonStatus || STATUS_STYLES[item.syncStatus].label;
    const note = item.warning || metadata?.syncErrorMessage || undefined;

    if (metadata?.changeClassification === "new" || item.syncStatus === "new") {
      groups[0].items.push({ id: item.id, name: item.name, itemType: item.itemType, statusLabel, note });
      continue;
    }
    if (metadata?.changeClassification === "changed" || metadata?.changeClassification === "rebid" || item.syncStatus === "changed") {
      groups[1].items.push({ id: item.id, name: item.name, itemType: item.itemType, statusLabel, note });
      continue;
    }
    if (item.pipelineStatus === "needs_review" || item.syncStatus === "needs_review" || item.syncStatus === "error") {
      groups[2].items.push({ id: item.id, name: item.name, itemType: item.itemType, statusLabel, note });
      continue;
    }
    if (metadata?.changeClassification === "removed_from_current_bid" || metadata?.archiveCandidate || item.syncStatus === "removed") {
      groups[3].items.push({ id: item.id, name: item.name, itemType: item.itemType, statusLabel, note });
    }
  }

  return groups.filter((group) => group.items.length > 0);
}

function extractSortableShotCode(value: string) {
  const directCode = value.trim();
  if (/^[A-Za-z]+_\d+_\d+_\d+$/.test(directCode)) return directCode;
  const match = directCode.match(/^([A-Za-z]+_\d+_\d+_\d+)/);
  return match?.[1] || directCode;
}

function compareReviewItemNames(a: { name: string; parentName?: string }, b: { name: string; parentName?: string }) {
  const aCode = extractSortableShotCode(a.parentName || a.name);
  const bCode = extractSortableShotCode(b.parentName || b.name);
  const aParsed = parseShotCode(aCode);
  const bParsed = parseShotCode(bCode);

  const numericCompare =
    (aParsed.seasonNumber ?? Number.MAX_SAFE_INTEGER) - (bParsed.seasonNumber ?? Number.MAX_SAFE_INTEGER) ||
    (aParsed.episodeNumber ?? Number.MAX_SAFE_INTEGER) - (bParsed.episodeNumber ?? Number.MAX_SAFE_INTEGER) ||
    (aParsed.sequenceNumber ?? Number.MAX_SAFE_INTEGER) - (bParsed.sequenceNumber ?? Number.MAX_SAFE_INTEGER) ||
    (aParsed.shotNumber ?? Number.MAX_SAFE_INTEGER) - (bParsed.shotNumber ?? Number.MAX_SAFE_INTEGER);

  if (numericCompare !== 0) return numericCompare;
  if (aCode !== bCode) return aCode.localeCompare(bCode, undefined, { numeric: true, sensitivity: "base" });
  return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
}

function isExcludedStructureReviewItem(item: FptStructureIssue) {
  if (item.entityType !== "shot") return false;
  const parsed = parseShotCode(item.name);
  const parts = String(parsed.normalized || item.name || "").trim().toLowerCase().split("_");
  return parts.length >= 4 && parts[2] === "clt";
}

function isDuplicateFptTaskIssue(item: FptStructureIssue) {
  return item.entityType === "task" && /multiple fpt tasks matched/i.test(item.note || "");
}

function buildItemRow(
  item: Shot | Asset,
  itemType: "shot" | "asset",
  metadata: FptEntitySyncMetadata | undefined,
  setup: ProjectSetup,
  rates: RateCard,
  assetRates: AssetRateCard
): ItemRow {
  const progress = String(metadata?.lastKnownFptStatus || item.progressStatus || "").trim();
  const invoice = computeInvoiceReadiness(item, metadata);
  const warning =
    metadata?.syncErrorMessage ||
    (Number(metadata?.tasksMissingPipelineStep || 0) > 0
      ? `${metadata?.tasksMissingPipelineStep} task${Number(metadata?.tasksMissingPipelineStep) === 1 ? "" : "s"} missing Pipeline Steps`
      : undefined);
  const bidValue = resolveBidValue(item, itemType, setup, rates, assetRates);
  const profitability = metadata?.profitability;
  const parsedShotCode = itemType === "shot" ? parseShotCode((item as Shot).shotCode) : null;
  const shotEpisodeNumber = parsedShotCode?.episodeNumber;
  const groupLabel =
    itemType === "shot"
      ? typeof shotEpisodeNumber === "number" && Number.isFinite(shotEpisodeNumber)
        ? `Episode ${shotEpisodeNumber}`
        : "Other Shots"
      : ((item as Asset).type || "Uncategorised Assets").trim() || "Uncategorised Assets";
  const groupOrder =
    itemType === "shot"
      ? typeof shotEpisodeNumber === "number" && Number.isFinite(shotEpisodeNumber)
        ? shotEpisodeNumber
        : Number.MAX_SAFE_INTEGER
      : Number.MAX_SAFE_INTEGER;
  const groupKey =
    itemType === "shot"
      ? `shot:${typeof shotEpisodeNumber === "number" && Number.isFinite(shotEpisodeNumber) ? shotEpisodeNumber : "other"}`
      : `asset:${groupLabel.toLowerCase()}`;

  return {
    id: item.id,
    itemType,
    name: itemType === "shot" ? (item as Shot).shotCode : (item as Asset).assetCode,
    groupKey,
    groupLabel,
    groupOrder,
    bidStatus: getBidStatusValue({ active: item.active, status: item.status }),
    fptStatusLabel: formatFptStatusLabel(progress),
    syncStatus: toDashboardSyncStatus(metadata),
    pipelineStatus: toPipelineDashboardStatus(metadata),
    invoiceStatus: toInvoiceStatus(item, metadata),
    lastContext: formatPullContext(metadata),
    warning,
    comparisonStatus: metadata?.changeClassification ? CHANGE_CLASSIFICATION_LABELS[metadata.changeClassification] : undefined,
    invoiceStatusNote: invoice.invoiceReadyReason || invoice.invoiceBlockedReason,
    invoiceReady: invoice.invoiceReady,
    completedInFpt: invoice.completedInFpt,
    alreadyInvoiced: invoice.alreadyInvoiced,
    totalLoggedHours: metadata?.financials?.totalLoggedHours ?? item.actualHoursTotal,
    totalCost: metadata?.financials?.totalCost,
    costReliabilityLevel: metadata?.financials?.costReliability,
    costReliability: formatCostReliability(metadata?.financials),
    financialContext: formatFinancialContext(metadata?.financials, item.actualHoursTotal),
    bidValue: profitability?.bidValue ?? bidValue,
    grossProfit: profitability?.grossProfit,
    grossMarginPct: profitability?.grossMarginPct,
    profitabilityStatus: profitability?.status,
    profitabilityReliabilityLevel: profitability?.profitabilityReliability,
    profitabilityReliability: formatProfitabilityReliability(profitability, metadata?.financials),
    profitabilityContext: formatProfitabilityContext(profitability, bidValue),
    profitabilityNote: profitability?.note,
  };
}

export function ShotGridDashboard({
  projectId,
  setup,
  rates,
  assetRates,
  shots,
  assets,
  fptProjectId,
  fptProjectName,
  lastPulledAt,
  financials,
  profitability,
  fptStructureInspection,
  onProjectMetadataRefresh,
}: ShotGridDashboardProps) {
  const { user } = useAuth();
  const [projectState, setProjectState] = useState<LocalProjectSnapshot>(() => {
    const nextEntitySyncMetadata = Object.fromEntries(
      [...shots.map((shot) => [shot.id, shot.syncMetadata]), ...assets.map((asset) => [asset.id, asset.syncMetadata])].filter(
        (entry): entry is [string, FptEntitySyncMetadata] => Boolean(entry[1])
      )
    );

    const fallbackState: LocalProjectSnapshot = {
      setup,
      rates,
      assetRates,
      shots,
      assets,
      fptProjectId,
      fptProjectName,
      fptLinked: Boolean(fptProjectId),
      lastPulledAt,
      financials,
      profitability,
      fptStructureInspection: fptStructureInspection || null,
      entitySyncMetadata: nextEntitySyncMetadata,
    };

    if (!projectId || typeof window === "undefined") {
      return fallbackState;
    }

    try {
      const raw = window.localStorage.getItem(`${SHOTGRID_SNAPSHOT_CACHE_STORAGE_KEY}:${projectId}`);
      if (!raw) return fallbackState;
      const cached = JSON.parse(raw) as Partial<LocalProjectSnapshot>;
      return {
        ...fallbackState,
        financials: pickNewerFinancials(cached.financials, fallbackState.financials),
        profitability: pickNewerProfitability(cached.profitability, fallbackState.profitability),
        fptStructureInspection: fallbackState.fptStructureInspection || cached.fptStructureInspection || null,
        lastPulledAt: fallbackState.lastPulledAt || cached.lastPulledAt,
        fptProjectId: fallbackState.fptProjectId || cached.fptProjectId,
        fptProjectName: fallbackState.fptProjectName || cached.fptProjectName,
        fptProjectUrl: cached.fptProjectUrl,
        fptLinked: fallbackState.fptLinked ?? cached.fptLinked,
      };
    } catch {
      return fallbackState;
    }
  });
  const [isLoadingProject, setIsLoadingProject] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  const [isSyncingTasks, setIsSyncingTasks] = useState(false);
  const [isSyncingProjectSupervision, setIsSyncingProjectSupervision] = useState(false);
  const [isInspectingStructure, setIsInspectingStructure] = useState(false);
  const [isPreparingRepair, setIsPreparingRepair] = useState(false);
  const [isApplyingRepair, setIsApplyingRepair] = useState(false);
  const [repairPlan, setRepairPlan] = useState<FptRepairPlan | null>(null);
  const [structureInspection, setStructureInspection] = useState<FptStructureInspection | null>(fptStructureInspection || null);
  const [expandedStructureGroups, setExpandedStructureGroups] = useState<Record<string, boolean>>({});
  const [collapsedBidScopeGroups, setCollapsedBidScopeGroups] = useState<Record<string, boolean>>({});
  const [structureReviewFilter, setStructureReviewFilter] = useState<StructureReviewFilter>("all");
  const [itemFilter, setItemFilter] = useState<ItemFilter>("all");
  const [structureActionId, setStructureActionId] = useState<string | null>(null);
  const [isStructureReviewVisible, setIsStructureReviewVisible] = useState(true);
  const structureInspectionRef = useRef<HTMLDivElement | null>(null);

  const loadProjectState = useCallback(async () => {
    if (!projectId || !user?.uid) return;
    setIsLoadingProject(true);
    try {
      const res = await fetch(`/api/projects/${projectId}`, { headers: { "x-user-uid": user.uid } });
      if (!res.ok) throw new Error("Failed to load project state.");
      const data = await res.json();
      setProjectState({
        setup: data.setup || setup,
        rates: data.rates || rates,
        assetRates: data.assetRates || assetRates,
        shots: data.shots || [],
        assets: data.assets || [],
        fptProjectId: data.fptProjectId ? String(data.fptProjectId) : null,
        fptProjectName: data.fptProjectName || null,
        fptProjectUrl: data.fptProjectUrl,
        fptLinked: data.fptLinked ?? Boolean(data.fptProjectId),
        lastPulledAt: data.lastPulledAt,
        lastPushedAt: data.lastPushedAt,
        syncStatus: data.syncStatus,
        entitySyncMetadata: data.entitySyncMetadata || {},
        financials: data.financials,
        profitability: data.profitability,
        fptStructureInspection: data.fptStructureInspection || null,
      });
      setStructureInspection(data.fptStructureInspection || null);
    } catch (error: any) {
      toast.error(error.message || "Failed to refresh ShotGrid project state.");
    } finally {
      setIsLoadingProject(false);
    }
  }, [projectId, setup, user?.uid]);

  useEffect(() => {
    setProjectState((current) => {
      const nextEntitySyncMetadata = Object.fromEntries(
        [...shots.map((shot) => [shot.id, shot.syncMetadata]), ...assets.map((asset) => [asset.id, asset.syncMetadata])].filter(
          (entry): entry is [string, FptEntitySyncMetadata] => Boolean(entry[1])
        )
      );

      return {
        setup,
        rates,
        assetRates,
        shots,
        assets,
        fptProjectId,
        fptProjectName: current.fptProjectName,
        fptProjectUrl: current.fptProjectUrl,
        fptLinked: current.fptLinked ?? Boolean(fptProjectId),
        lastPulledAt: current.lastPulledAt || lastPulledAt,
        lastPushedAt: current.lastPushedAt,
        syncStatus: current.syncStatus,
        entitySyncMetadata: Object.keys(current.entitySyncMetadata || {}).length > 0 ? current.entitySyncMetadata : nextEntitySyncMetadata,
        // Preserve the richer pulled state while parent props catch up, otherwise the
        // project snapshot can briefly regress to an empty fallback even though the
        // saved project already has real timelog totals and profitability.
        financials: pickNewerFinancials(current.financials, financials),
        profitability: pickNewerProfitability(current.profitability, profitability),
        fptStructureInspection: current.fptStructureInspection || fptStructureInspection || null,
      };
    });
  }, [assetRates, assets, financials, fptProjectId, fptStructureInspection, lastPulledAt, profitability, rates, setup, shots]);

  useEffect(() => {
    void loadProjectState();
  }, [loadProjectState]);

  useEffect(() => {
    if (!projectId) return;
    try {
      const raw = window.localStorage.getItem(`${STRUCTURE_REVIEW_VISIBILITY_STORAGE_KEY}:${projectId}`);
      if (raw === "hidden") {
        setIsStructureReviewVisible(false);
        return;
      }
      if (raw === "visible") {
        setIsStructureReviewVisible(true);
        return;
      }
    } catch {
      // Ignore local storage access problems and fall back to the default visible state.
    }
    setIsStructureReviewVisible(true);
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    try {
      window.localStorage.setItem(
        `${SHOTGRID_SNAPSHOT_CACHE_STORAGE_KEY}:${projectId}`,
        JSON.stringify({
          fptProjectId: projectState.fptProjectId,
          fptProjectUrl: projectState.fptProjectUrl,
          fptLinked: projectState.fptLinked,
          lastPulledAt: projectState.lastPulledAt,
          financials: projectState.financials,
          profitability: projectState.profitability,
          fptStructureInspection: projectState.fptStructureInspection || structureInspection || null,
        })
      );
    } catch {
      // Ignore local cache write failures and continue with in-memory state only.
    }
  }, [
    projectId,
    projectState.financials,
    projectState.fptLinked,
    projectState.fptProjectId,
    projectState.fptProjectUrl,
    projectState.fptStructureInspection,
    projectState.lastPulledAt,
    projectState.profitability,
    structureInspection,
  ]);

  useEffect(() => {
    if (!projectId) return;
    try {
      window.localStorage.setItem(
        `${STRUCTURE_REVIEW_VISIBILITY_STORAGE_KEY}:${projectId}`,
        isStructureReviewVisible ? "visible" : "hidden"
      );
    } catch {
      // Ignore local storage write failures; the review still works within the current session.
    }
  }, [isStructureReviewVisible, projectId]);

  useEffect(() => {
    if (!onProjectMetadataRefresh) return;
    onProjectMetadataRefresh({
      fptProjectId: projectState.fptProjectId || null,
      fptProjectName: projectState.fptProjectName || null,
      fptProjectUrl: projectState.fptProjectUrl || null,
      fptLinked: projectState.fptLinked,
      lastPulledAt: projectState.lastPulledAt,
      financials: projectState.financials,
      profitability: projectState.profitability,
      fptStructureInspection: projectState.fptStructureInspection || structureInspection || null,
    });
  }, [
    onProjectMetadataRefresh,
    projectState.financials,
    projectState.fptLinked,
    projectState.fptProjectId,
    projectState.fptProjectName,
    projectState.fptProjectUrl,
    projectState.fptStructureInspection,
    projectState.lastPulledAt,
    projectState.profitability,
    structureInspection,
  ]);

  const activeSetup = projectState.setup || setup;
  const activeRates = projectState.rates || rates;
  const activeAssetRates = projectState.assetRates || assetRates;
  const activeShots = projectState.shots || shots;
  const activeAssets = projectState.assets || assets;
  const entitySyncMetadata = projectState.entitySyncMetadata || {};

  const itemRows = useMemo(() => {
    const shotRows = activeShots.map((shot) =>
      buildItemRow(shot, "shot", entitySyncMetadata[shot.id] || shot.syncMetadata, activeSetup, activeRates, activeAssetRates)
    );
    const assetRows = activeAssets.map((asset) =>
      buildItemRow(asset, "asset", entitySyncMetadata[asset.id] || asset.syncMetadata, activeSetup, activeRates, activeAssetRates)
    );
    return [...shotRows, ...assetRows].sort((a, b) => a.name.localeCompare(b.name));
  }, [activeAssetRates, activeAssets, activeRates, activeSetup, activeShots, entitySyncMetadata]);

  const hasLink = Boolean(projectState.fptProjectId);
  const pushedCount = itemRows.filter((item) => item.syncStatus !== "not_pushed" && item.syncStatus !== "new").length;
  const inSyncCount = itemRows.filter((item) => item.syncStatus === "in_sync").length;
  const completedInFptCount = itemRows.filter((item) => item.completedInFpt).length;
  const invoiceReadyCount = itemRows.filter((item) => item.invoiceReady).length;
  const alreadyInvoicedCount = itemRows.filter((item) => item.alreadyInvoiced).length;
  const pipelineIssueCount = itemRows.reduce((sum, item) => {
    const metadata = entitySyncMetadata[item.id];
    return sum + Number(metadata?.tasksMissingPipelineStep || 0);
  }, 0);
  const impactedPipelineItems = itemRows.filter((item) => item.pipelineStatus === "needs_review").length;
  const projectFinancials =
    projectState.financials || {
      totalLoggedHours: itemRows.reduce((sum, item) => sum + (item.totalLoggedHours || 0), 0),
      totalCost: itemRows.reduce((sum, item) => sum + (item.totalCost || 0), 0) || undefined,
      costReliability:
        itemRows.reduce((sum, item) => sum + (item.totalLoggedHours || 0), 0) <= 0
          ? "low"
          : itemRows.some((item) => item.costReliabilityLevel === "low")
            ? "low"
            : itemRows.some((item) => item.costReliabilityLevel === "medium")
              ? "medium"
              : "high",
      provisional: itemRows.some((item) => item.costReliabilityLevel === "medium" || item.costReliabilityLevel === "low"),
    };
  const projectProfitability =
    projectState.profitability || {
      bidValue: itemRows.reduce((sum, item) => sum + (item.bidValue || 0), 0) || undefined,
      totalCost: itemRows.reduce((sum, item) => sum + (item.totalCost || 0), 0) || undefined,
      grossProfit:
        itemRows.reduce((sum, item) => sum + (item.grossProfit || 0), 0) || undefined,
      grossMarginPct: undefined,
      profitabilityReliability:
        itemRows.some((item) => item.profitabilityReliabilityLevel === "low")
          ? "low"
          : itemRows.some((item) => item.profitabilityReliabilityLevel === "medium")
            ? "medium"
            : "high",
      provisional: itemRows.some((item) => item.profitabilityReliabilityLevel && item.profitabilityReliabilityLevel !== "high"),
      insufficientData: !itemRows.some((item) => typeof item.grossProfit === "number"),
      note: !itemRows.some((item) => typeof item.grossProfit === "number") ? "Insufficient cost data" : undefined,
      status: undefined,
      lastComputedAt: projectState.lastPulledAt,
    };
  const reviewGroups = buildReviewGroups(itemRows, entitySyncMetadata);
  const visibleStructureInspection = useMemo(() => {
    if (!structureInspection) return null;
    return {
      ...structureInspection,
      appOnly: structureInspection.appOnly.filter((item) => !isExcludedStructureReviewItem(item)),
      fptOnly: structureInspection.fptOnly.filter((item) => !isExcludedStructureReviewItem(item)),
      ambiguous: structureInspection.ambiguous.filter((item) => !isExcludedStructureReviewItem(item)),
    };
  }, [structureInspection]);
  const structureIssueCount =
    (visibleStructureInspection?.appOnly.length || 0) +
    (visibleStructureInspection?.fptOnly.length || 0) +
    (visibleStructureInspection?.ambiguous.length || 0);
  const structureReviewIssues = useMemo(() => {
    if (!visibleStructureInspection) return [];
    return [
      ...visibleStructureInspection.appOnly,
      ...visibleStructureInspection.fptOnly,
      ...visibleStructureInspection.ambiguous,
    ];
  }, [visibleStructureInspection]);
  const filteredStructureIssues = useMemo(() => {
    if (structureReviewFilter === "all") return structureReviewIssues;
    if (structureReviewFilter === "shots") {
      return structureReviewIssues.filter((item) => item.entityType === "shot" || item.entityType === "asset");
    }
    if (structureReviewFilter === "tasks") {
      return structureReviewIssues.filter((item) => item.entityType === "task");
    }
    if (structureReviewFilter === "only_in_app") {
      return structureReviewIssues.filter((item) => item.side === "app_only");
    }
    if (structureReviewFilter === "only_in_fpt") {
      return structureReviewIssues.filter((item) => item.side === "fpt_only");
    }
    if (structureReviewFilter === "duplicates") {
      return structureReviewIssues.filter((item) => isDuplicateFptTaskIssue(item));
    }
    if (structureReviewFilter === "needs_manual_review") {
      return structureReviewIssues.filter((item) => item.side === "ambiguous");
    }
    return structureReviewIssues;
  }, [structureReviewFilter, structureReviewIssues]);
  const structureFilterCounts = useMemo(
    () => ({
      all: structureReviewIssues.length,
      shots: structureReviewIssues.filter((item) => item.entityType === "shot" || item.entityType === "asset").length,
      tasks: structureReviewIssues.filter((item) => item.entityType === "task").length,
      only_in_app: structureReviewIssues.filter((item) => item.side === "app_only").length,
      only_in_fpt: structureReviewIssues.filter((item) => item.side === "fpt_only").length,
      duplicates: structureReviewIssues.filter((item) => isDuplicateFptTaskIssue(item)).length,
      needs_manual_review: structureReviewIssues.filter((item) => item.side === "ambiguous").length,
    }),
    [structureReviewIssues]
  );
  const structureReviewGroups = useMemo<StructureReviewGroup[]>(() => {
    if (!visibleStructureInspection) return [];

    const shotMismatches = filteredStructureIssues
      .filter((item) => item.entityType === "shot" || item.entityType === "asset")
      .sort(compareReviewItemNames);

    const taskMismatches = filteredStructureIssues
      .filter((item) => item.entityType === "task" && !isDuplicateFptTaskIssue(item))
      .sort(compareReviewItemNames);

    const duplicateFptTasks = filteredStructureIssues
      .filter((item) => isDuplicateFptTaskIssue(item))
      .sort(compareReviewItemNames);

    return [
      {
        title: "Shot Mismatches",
        description: "Shots or assets that are missing from one side, or still need a manual decision.",
        items: shotMismatches,
        tone: "border-amber-200 bg-amber-50 text-amber-900",
        counts: formatStructureIssueCounts(shotMismatches),
      },
      {
        title: "Task Mismatches",
        description: "Tasks that are missing from one side, or still cannot be linked safely.",
        items: taskMismatches,
        tone: "border-sky-200 bg-sky-50 text-sky-900",
        counts: formatStructureIssueCounts(taskMismatches),
      },
      {
        title: "Duplicate FPT Tasks",
        description: "Same-named FPT tasks under one parent that still need a manual decision.",
        items: duplicateFptTasks,
        tone: "border-rose-200 bg-rose-50 text-rose-900",
        counts: formatStructureIssueCounts(duplicateFptTasks),
      },
    ];
  }, [filteredStructureIssues, visibleStructureInspection]);
  const toggleStructureGroup = useCallback((groupTitle: string) => {
    setExpandedStructureGroups((current) => ({
      ...current,
      [groupTitle]: !current[groupTitle],
    }));
  }, []);
  const toggleBidScopeGroup = useCallback((groupKey: string) => {
    setCollapsedBidScopeGroups((current) => ({
      ...current,
      [groupKey]: !current[groupKey],
    }));
  }, []);
  const visibleItemRows = useMemo(() => {
    if (itemFilter === "invoice_ready") return itemRows.filter((item) => item.invoiceReady);
    if (itemFilter === "blocked") return itemRows.filter((item) => !item.invoiceReady);
    if (itemFilter === "profitable") return itemRows.filter((item) => item.profitabilityStatus === "profitable");
    if (itemFilter === "loss_making") return itemRows.filter((item) => item.profitabilityStatus === "loss_making");
    if (itemFilter === "low_confidence") {
      return itemRows.filter((item) => item.profitabilityReliabilityLevel === "low" || item.profitabilityStatus === "insufficient_data");
    }
    return itemRows;
  }, [itemFilter, itemRows]);
  const bidScopeCollections = useMemo(() => {
    const shotRows = visibleItemRows.filter((item) => item.itemType === "shot");
    const assetRows = visibleItemRows.filter((item) => item.itemType === "asset");

    const buildGroups = (rows: ItemRow[]) =>
      Array.from(
        rows.reduce((map, row) => {
          const existing = map.get(row.groupKey);
          if (existing) {
            existing.rows.push(row);
          } else {
            map.set(row.groupKey, {
              key: row.groupKey,
              label: row.groupLabel,
              order: row.groupOrder,
              rows: [row],
            });
          }
          return map;
        }, new Map<string, { key: string; label: string; order: number; rows: ItemRow[] }>())
          .values()
      )
        .map((group) => ({
          ...group,
          rows: [...group.rows].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })),
        }))
        .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" }));

    return {
      shotGroups: buildGroups(shotRows),
      assetGroups: buildGroups(assetRows),
    };
  }, [visibleItemRows]);

  const openInFptUrl = projectState.fptProjectUrl || (hasLink ? `${"https://vinefx.shotgunstudio.com"}/detail/Project/${projectState.fptProjectId}` : "");

  const handlePullLatest = async () => {
    if (!projectId || !user?.uid) return;
    if (!hasLink) {
      toast.message("Nothing is linked in FPT yet.", {
        description: "Push the bid first, then pullback can retrieve real production state.",
      });
      return;
    }

    setIsPulling(true);
    try {
      const response = await fetch(`/api/fpt/projects/${projectId}/pull`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
        },
        body: JSON.stringify({}),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Failed to pull latest data from FPT.");
      await loadProjectState();
      setRepairPlan(null);
      toast.success("Pulled latest production state from FPT.", {
        description: payload.result?.summary?.tasksMissingPipelineStep
          ? `${payload.result.summary.tasksMissingPipelineStep} task${payload.result.summary.tasksMissingPipelineStep === 1 ? "" : "s"} still need Pipeline Steps. ${payload.result?.timelogs?.syncedCount || 0} time log${payload.result?.timelogs?.syncedCount === 1 ? "" : "s"} refreshed.`
          : `${payload.result?.timelogs?.syncedCount || 0} time log${payload.result?.timelogs?.syncedCount === 1 ? "" : "s"} refreshed alongside the latest linked FPT state.`,
      });
    } catch (error: any) {
      toast.error(error.message || "Failed to pull latest data from FPT.");
    } finally {
      setIsPulling(false);
    }
  };

  const handlePrepareRepair = async () => {
    if (!projectId || !user?.uid) return;
    if (!hasLink) {
      toast.message("Nothing is linked in FPT yet.", {
        description: "There are no linked FPT tasks to repair yet.",
      });
      return;
    }

    setIsPreparingRepair(true);
    try {
      const response = await fetch(`/api/fpt/projects/${projectId}/repair-pipeline-steps`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
        },
        body: JSON.stringify({ dryRun: true }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Failed to prepare pipeline-step repair plan.");
      setRepairPlan(payload.plan || null);
      await loadProjectState();
      toast.success("Prepared pipeline-step repair plan.", {
        description: payload.plan?.repairableCount
          ? `${payload.plan.repairableCount} task${payload.plan.repairableCount === 1 ? "" : "s"} can be fixed automatically.`
          : "No automatic pipeline-step repairs were proposed.",
      });
    } catch (error: any) {
      toast.error(error.message || "Failed to prepare pipeline-step repair plan.");
    } finally {
      setIsPreparingRepair(false);
    }
  };

  const handleSyncTasks = async () => {
    if (!projectId || !user?.uid) return;
    if (!hasLink) {
      toast.message("Nothing is linked in FPT yet.", {
        description: "Link or create the FPT project first, then task write-back can update the existing tasks.",
      });
      return;
    }

    setIsSyncingTasks(true);
    try {
      const response = await fetch(`/api/fpt/projects/${projectId}/sync/tasks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
        },
        body: JSON.stringify({}),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Failed to sync tasks to FPT.");
      await loadProjectState();
      const syncedCount = Array.isArray(payload.result) ? payload.result.length : 0;
      const changedCount = Array.isArray(payload.result) ? payload.result.filter((item: any) => item?.changed).length : 0;
      toast.success("Refreshed task rate, bid, and duration data in FPT.", {
        description: changedCount > 0
          ? `${changedCount} task${changedCount === 1 ? "" : "s"} updated in FPT with Rate, Bid, and Duration.`
          : `${syncedCount} task${syncedCount === 1 ? "" : "s"} checked with no changes needed.`,
      });
    } catch (error: any) {
      toast.error(error.message || "Failed to sync task rate, bid, and duration data to FPT.");
    } finally {
      setIsSyncingTasks(false);
    }
  };

  const handleSyncProjectLevelTasks = async () => {
    if (!projectId || !user?.uid) return;
    if (!hasLink) {
      toast.message("Nothing is linked in FPT yet.", {
        description: "Link or create the FPT project first, then we can push the project-level supervision and production tasks.",
      });
      return;
    }

    setIsSyncingProjectSupervision(true);
    try {
      const response = await fetch(`/api/fpt/projects/${projectId}/sync/project-level-tasks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
        },
        body: JSON.stringify({}),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Failed to sync project-level tasks to FPT.");
      await loadProjectState();
      const results = Array.isArray(payload.result) ? payload.result : [];
      const supervisionResult = results.find((item: any) => item?.taskType === "supervisor");
      const productionResult = results.find((item: any) => item?.taskType === "production");
      const editorialResult = results.find((item: any) => item?.taskType === "editorial");
      const formattedBidDays =
        typeof supervisionResult?.bidDays === "number"
          ? Number(supervisionResult.bidDays.toFixed(2))
          : undefined;
      toast.success("Synced project-level tasks to FPT.", {
        description:
          typeof formattedBidDays === "number"
            ? `${formattedBidDays} bid day${formattedBidDays === 1 ? "" : "s"} on Supervision${typeof supervisionResult?.blendedDailyRate === "number" ? ` at £${supervisionResult.blendedDailyRate}/day` : ""}. ${productionResult ? "Production checked." : ""}${editorialResult ? " Editorial checked." : ""}`.trim()
            : `Project-level tasks checked in FPT.${productionResult ? " Production included." : ""}${editorialResult ? " Editorial included." : ""}`,
      });
    } catch (error: any) {
      toast.error(error.message || "Failed to sync project-level tasks to FPT.");
    } finally {
      setIsSyncingProjectSupervision(false);
    }
  };

  const handleInspectStructure = async () => {
    if (!projectId || !user?.uid) return;
    if (!hasLink) {
      toast.message("Nothing is linked in FPT yet.", {
        description: "Link the project to an existing FPT project first, then inspect the structure.",
      });
      return;
    }

    setIsInspectingStructure(true);
    toast.message("Inspecting existing FPT structure...", {
      description: "We’re checking shots, assets, and tasks for exact matches. This can take a minute on larger projects.",
    });
    try {
      const response = await fetch(`/api/fpt/projects/${projectId}/inspect-existing-structure`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
        },
        body: JSON.stringify({}),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Failed to inspect the existing FPT structure.");
      setStructureInspection(payload.result || null);
      setIsStructureReviewVisible(true);
      await loadProjectState();
      requestAnimationFrame(() => {
        structureInspectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      const summary = payload.result?.summary;
      const issueCount =
        Number(summary?.appOnlyShots || 0) +
        Number(summary?.appOnlyAssets || 0) +
        Number(summary?.appOnlyTasks || 0) +
        Number(summary?.fptOnlyShots || 0) +
        Number(summary?.fptOnlyAssets || 0) +
        Number(summary?.fptOnlyTasks || 0) +
        Number(summary?.ambiguousShots || 0) +
        Number(summary?.ambiguousAssets || 0) +
        Number(summary?.ambiguousTasks || 0);
      toast.success("Inspected existing FPT structure.", {
        description: issueCount > 0
          ? `${summary?.matchedShots || 0} shots, ${summary?.matchedAssets || 0} assets, and ${summary?.matchedTasks || 0} tasks linked exactly. ${issueCount} item${issueCount === 1 ? "" : "s"} still need review.`
          : `Exact local links were stored for ${summary?.matchedShots || 0} shots, ${summary?.matchedAssets || 0} assets, and ${summary?.matchedTasks || 0} tasks.`,
      });
    } catch (error: any) {
      toast.error(error.message || "Failed to inspect the existing FPT structure.");
    } finally {
      setIsInspectingStructure(false);
    }
  };

  const handleCreateIssueInFpt = async (issue: FptStructureIssue) => {
    if (!projectId || !user?.uid || !issue.localEntityId || issue.side !== "app_only") return;

    const route =
      issue.entityType === "shot"
        ? `/api/fpt/projects/${projectId}/shots/${issue.localEntityId}/sync`
        : issue.entityType === "asset"
          ? `/api/fpt/projects/${projectId}/assets/${issue.localEntityId}/sync`
          : `/api/fpt/projects/${projectId}/tasks/${issue.localEntityId}/sync`;

    setStructureActionId(issue.id);
    try {
      const response = await fetch(route, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
        },
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Failed to create the item in FPT.");
      await handleInspectStructure();
      toast.success("Created in FPT.", {
        description: `${issue.name} was pushed to FPT and the structure review was refreshed.`,
      });
    } catch (error: any) {
      toast.error(error.message || "Failed to create the item in FPT.");
    } finally {
      setStructureActionId(null);
    }
  };

  const handleApplyRepair = async () => {
    if (!projectId || !user?.uid || !repairPlan) return;
    setIsApplyingRepair(true);
    try {
      const response = await fetch(`/api/fpt/projects/${projectId}/repair-pipeline-steps`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
        },
        body: JSON.stringify({ dryRun: false }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Failed to apply pipeline-step repairs.");
      setRepairPlan(null);
      await loadProjectState();
      toast.success("Applied pipeline-step repairs.", {
        description: `${payload.result?.appliedCount || 0} task${payload.result?.appliedCount === 1 ? "" : "s"} updated in FPT.`,
      });
    } catch (error: any) {
      toast.error(error.message || "Failed to apply pipeline-step repairs.");
    } finally {
      setIsApplyingRepair(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
        <SummaryCard
          title="FPT Connection Status"
          value={hasLink ? "Linked" : "Not Linked"}
          detail={
            hasLink
              ? projectState.fptProjectName && projectState.fptProjectId
                ? `Project is linked to Flow Production Tracking as ${projectState.fptProjectName} (${projectState.fptProjectId}).`
                : `Project is linked to Flow Production Tracking${projectState.fptProjectId ? ` as ${projectState.fptProjectId}` : ""}.`
              : "Nothing exists in FPT yet for this project. The next step is still a controlled first-time push."
          }
          tone={hasLink ? "emerald" : "slate"}
        />
        <SummaryCard
          title="Push / Sync Status"
          value={projectState.syncStatus === "error" ? "Error" : pushedCount > 0 ? `${inSyncCount}/${itemRows.length} synced` : "Not Pushed"}
          detail={
            projectState.lastPulledAt
              ? `Last pulled ${new Date(projectState.lastPulledAt).toLocaleString()}. Differences are surfaced before any action is taken.`
              : "No pullback has run yet. Use Pull Latest from FPT to populate real production state."
          }
          tone={projectState.syncStatus === "error" ? "rose" : pushedCount > 0 ? "blue" : "slate"}
        />
        <SummaryCard
          title="Production Status"
          value={pipelineIssueCount > 0 ? `${impactedPipelineItems} items need review` : activeSetup.progressStatusSummary || activeSetup.progressStatus || "Pending"}
          detail={`${itemRows.length} shot and asset items are visible from the current bid scope.`}
          tone={pipelineIssueCount > 0 ? "amber" : itemRows.length > 0 ? "violet" : "slate"}
        />
        <SummaryCard
          title="Financial Snapshot"
          value={getProjectFinancialSnapshotValue(projectFinancials, projectState.lastPulledAt)}
          detail={getProjectFinancialSnapshotDetail(projectFinancials, invoiceReadyCount, projectState.lastPulledAt)}
          tone={
            projectFinancials.costReliability === "high"
              ? "emerald"
              : projectFinancials.totalLoggedHours
                ? "amber"
                : completedInFptCount > 0
                  ? "violet"
                  : "amber"
          }
        />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Profitability Snapshot</h3>
            <p className="mt-1 text-xs text-slate-600">
              Operational profitability compares app-owned bid values against pulled FPT cost. Treat provisional or reconciliation-led figures as guidance, not final accounts.
            </p>
            {formatProfitabilityTimestamp(projectProfitability, projectState.lastPulledAt) && (
              <p className="mt-1 text-[11px] font-medium text-slate-500">
                {formatProfitabilityTimestamp(projectProfitability, projectState.lastPulledAt)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <ProfitabilityBadge status={projectProfitability.status} />
            {projectProfitability.profitabilityReliability && (
              <span className="text-xs font-medium text-slate-500">{formatProfitabilityReliability(projectProfitability, projectFinancials)}</span>
            )}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 xl:grid-cols-4">
          <SummaryCard title="Bid" value={formatCurrency(projectProfitability.bidValue) || "—"} detail="Awarded value only" tone="blue" />
          <SummaryCard
            title="Cost"
            value={formatCurrency(projectProfitability.totalCost) || "—"}
            detail={[
              projectProfitability.provisional ? "Provisional cost coverage" : "App-calculated live cost",
              typeof projectProfitability.taskCost === "number" ? `Task cost ${formatCurrency(projectProfitability.taskCost)}` : null,
              typeof projectProfitability.supervisionCost === "number"
                ? `Supervision ${formatCurrency(projectProfitability.supervisionCost)}`
                : null,
            ]
              .filter(Boolean)
              .join(" • ")}
            tone={projectProfitability.provisional ? "amber" : "slate"}
          />
          <SummaryCard
            title="Gross Profit"
            value={formatCurrency(projectProfitability.grossProfit) || "—"}
            detail={projectProfitability.note || "Bid minus internal cost"}
            tone={projectProfitability.status === "loss_making" ? "rose" : projectProfitability.status === "profitable" ? "emerald" : "amber"}
          />
          <SummaryCard
            title="Margin"
            value={formatMargin(projectProfitability.grossMarginPct) || "Insufficient data"}
            detail={formatProfitabilityReliability(projectProfitability, projectFinancials) || "Awaiting reliable cost coverage"}
            tone={projectProfitability.status === "loss_making" ? "rose" : projectProfitability.status === "profitable" ? "emerald" : "amber"}
          />
        </div>
      </div>

      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Actions</h3>
          <p className="text-xs text-slate-600">Keep FPT state current, refresh task budgeting, and only repair pipeline steps when they actually need attention.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void handlePullLatest()}
            disabled={isPulling || isLoadingProject || isSyncingTasks || isInspectingStructure}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-900 px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw className={cn("h-4 w-4", isPulling && "animate-spin")} />
            Pull Latest from FPT
          </button>
          <button
            type="button"
            onClick={() => void handleInspectStructure()}
            disabled={isInspectingStructure || isLoadingProject || isPulling || isSyncingTasks || isSyncingProjectSupervision}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isInspectingStructure ? <RefreshCw className="h-4 w-4 animate-spin" /> : <BadgeCheck className="h-4 w-4" />}
            {isInspectingStructure ? "Inspecting FPT Structure..." : "Inspect Existing FPT Structure"}
          </button>
          <button
            type="button"
            onClick={() => void handleSyncTasks()}
            disabled={isSyncingTasks || isSyncingProjectSupervision || isLoadingProject || isPulling || isInspectingStructure}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw className={cn("h-4 w-4", isSyncingTasks && "animate-spin")} />
            Refresh Task Rate + Bid + Duration
          </button>
          <button
            type="button"
            onClick={() => void handleSyncProjectLevelTasks()}
            disabled={isSyncingProjectSupervision || isSyncingTasks || isLoadingProject || isPulling || isInspectingStructure}
            className="inline-flex items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3.5 py-2 text-xs font-semibold text-violet-800 transition hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw className={cn("h-4 w-4", isSyncingProjectSupervision && "animate-spin")} />
            Push Project Level Tasks
          </button>
          {pipelineIssueCount > 0 && (
            <button
              type="button"
              onClick={() => void handlePrepareRepair()}
              disabled={isPreparingRepair || isApplyingRepair || isLoadingProject || isSyncingTasks || isSyncingProjectSupervision}
              className="inline-flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2 text-xs font-semibold text-amber-800 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Wrench className="h-4 w-4" />
              Repair Pipeline Steps
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (!openInFptUrl) return;
              window.open(openInFptUrl, "_blank", "noopener,noreferrer");
            }}
            disabled={!openInFptUrl}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            <ExternalLink className="h-4 w-4" />
            Open in FPT
          </button>
        </div>
      </div>

      {visibleStructureInspection && (
        <div ref={structureInspectionRef} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Existing FPT Structure Review</h3>
              <p className="mt-1 text-xs text-slate-600">
                Exact unique matches have been linked locally. Anything listed below still needs review before we trust this project for pullbacks, timelogs, and write-back.
              </p>
            </div>
            <div className="flex flex-col items-end gap-2 text-right text-xs text-slate-500">
              <button
                type="button"
                onClick={() => setIsStructureReviewVisible((current) => !current)}
                className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600 transition hover:bg-slate-50"
              >
                {isStructureReviewVisible ? "Hide Review" : "Show Review"}
              </button>
              <div>
                <div>{structureIssueCount > 0 ? `${structureIssueCount} review item${structureIssueCount === 1 ? "" : "s"}` : "No anomalies found"}</div>
                <div className="mt-1">Last run {new Date(visibleStructureInspection.inspectedAt).toLocaleString()}</div>
              </div>
            </div>
          </div>

          {isStructureReviewVisible && (
            <>
              <div className="mt-4 grid grid-cols-2 gap-3 xl:grid-cols-6">
                <SummaryCard title="Matched Shots" value={String(visibleStructureInspection.summary.matchedShots)} detail="Exact shot-code links stored" tone="emerald" />
                <SummaryCard title="Matched Assets" value={String(visibleStructureInspection.summary.matchedAssets)} detail="Exact asset-code links stored" tone="emerald" />
                <SummaryCard title="Matched Tasks" value={String(visibleStructureInspection.summary.matchedTasks)} detail="Exact task-title links stored" tone="emerald" />
                <SummaryCard
                  title="Shot Mismatches"
                  value={String(structureReviewGroups[0]?.items.length || 0)}
                  detail="Shot or asset structure still differs"
                  tone={(structureReviewGroups[0]?.items.length || 0) > 0 ? "amber" : "slate"}
                />
                <SummaryCard
                  title="Task Mismatches"
                  value={String(structureReviewGroups[1]?.items.length || 0)}
                  detail="Task sets still differ across the two systems"
                  tone={(structureReviewGroups[1]?.items.length || 0) > 0 ? "amber" : "slate"}
                />
                <SummaryCard
                  title="Duplicate FPT Tasks"
                  value={String(structureReviewGroups[2]?.items.length || 0)}
                  detail="Same-name FPT tasks need manual review"
                  tone={(structureReviewGroups[2]?.items.length || 0) > 0 ? "rose" : "slate"}
                />
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {[
                  { key: "all" as const, label: `All (${structureFilterCounts.all})` },
                  { key: "shots" as const, label: `Shots (${structureFilterCounts.shots})` },
                  { key: "tasks" as const, label: `Tasks (${structureFilterCounts.tasks})` },
                  { key: "only_in_app" as const, label: `Only in App (${structureFilterCounts.only_in_app})` },
                  { key: "only_in_fpt" as const, label: `Only in FPT (${structureFilterCounts.only_in_fpt})` },
                  { key: "duplicates" as const, label: `Duplicates (${structureFilterCounts.duplicates})` },
                  { key: "needs_manual_review" as const, label: `Needs Manual Review (${structureFilterCounts.needs_manual_review})` },
                ].map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setStructureReviewFilter(option.key)}
                    className={cn(
                      "rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] transition",
                      structureReviewFilter === option.key
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {structureIssueCount > 0 && (
                <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
                  {structureReviewGroups.map((group) => (
                    <div key={group.title} className={cn("rounded-2xl border p-4", group.tone)}>
                      <h4 className="text-sm font-semibold">{group.title}</h4>
                      <p className="mt-1 text-xs opacity-80">{group.description}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {group.counts.missingInFpt > 0 && (
                          <span className="rounded-full border border-amber-200 bg-white/80 px-2.5 py-1 text-[11px] font-semibold text-amber-900">
                            {group.counts.missingInFpt} missing in FPT
                          </span>
                        )}
                        {group.counts.missingInApp > 0 && (
                          <span className="rounded-full border border-sky-200 bg-white/80 px-2.5 py-1 text-[11px] font-semibold text-sky-900">
                            {group.counts.missingInApp} missing in app
                          </span>
                        )}
                        {group.counts.awaitingAward > 0 && (
                          <span className="rounded-full border border-violet-200 bg-white/80 px-2.5 py-1 text-[11px] font-semibold text-violet-900">
                            {group.counts.awaitingAward} awaiting award
                          </span>
                        )}
                        {group.counts.deliveredLegacyDrift > 0 && (
                          <span className="rounded-full border border-orange-200 bg-white/80 px-2.5 py-1 text-[11px] font-semibold text-orange-900">
                            {group.counts.deliveredLegacyDrift} legacy drift
                          </span>
                        )}
                        {group.counts.needsManualReview > 0 && (
                          <span className="rounded-full border border-rose-200 bg-white/80 px-2.5 py-1 text-[11px] font-semibold text-rose-900">
                            {group.counts.needsManualReview} manual review
                          </span>
                        )}
                      </div>
                      <div className="mt-3 space-y-2">
                        {group.items.length === 0 ? (
                          <p className="text-xs opacity-80">Nothing in this bucket.</p>
                        ) : (
                          (expandedStructureGroups[group.title] ? group.items : group.items.slice(0, 8)).map((item) => (
                            <div key={item.id} className="rounded-xl border border-white/60 bg-white/70 p-3 text-xs">
                              <div className="flex items-start justify-between gap-3">
                                <div className="font-semibold">{item.name}</div>
                                <span
                                  className={cn(
                                    "shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold",
                                    getStructureIssueSideTone(item)
                                  )}
                                >
                                  {getStructureIssueSideLabel(item)}
                                </span>
                              </div>
                              <div className="mt-1 opacity-80">
                                {item.parentName ? `${item.parentName} · ` : ""}
                                {item.entityType}
                              </div>
                              <div className="mt-1 leading-5 opacity-90">{item.note}</div>
                              {item.side === "app_only" && item.localEntityId && (
                                <div className="mt-3 flex justify-end">
                                  <button
                                    type="button"
                                    onClick={() => void handleCreateIssueInFpt(item)}
                                    disabled={structureActionId === item.id}
                                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                  >
                                    <RefreshCw className={cn("h-3.5 w-3.5", structureActionId === item.id && "animate-spin")} />
                                    {structureActionId === item.id ? "Creating..." : "Create in FPT"}
                                  </button>
                                </div>
                              )}
                            </div>
                          ))
                        )}
                        {group.items.length > 8 && (
                          <button
                            type="button"
                            onClick={() => toggleStructureGroup(group.title)}
                            className="text-xs font-medium opacity-90 transition hover:opacity-100"
                          >
                            {expandedStructureGroups[group.title]
                              ? "Show less"
                              : `+${group.items.length - 8} more`}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!hasLink && (
        <div className="rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-white to-slate-50 p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                <Link2Off className="h-5 w-5" />
              </div>
              <div className="space-y-2">
                <h3 className="text-base font-semibold text-slate-900">No ShotGrid project is linked yet</h3>
                <p className="max-w-3xl text-xs leading-5 text-slate-600">
                  Nothing has been created in Flow Production Tracking for this project yet. The next controlled step will be to
                  push the current bid scope, then choose what should be created there first: project, shots, assets, tasks, and later
                  operational data.
                </p>
              </div>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">
              <ShieldCheck className="h-3.5 w-3.5" />
              Non-destructive
            </div>
          </div>
        </div>
      )}

      {pipelineIssueCount > 0 && (
        <WarningSurface
          title={`${pipelineIssueCount} task${pipelineIssueCount === 1 ? "" : "s"} missing Pipeline Steps`}
          detail={`${impactedPipelineItems} shot${impactedPipelineItems === 1 ? "" : "s"} or asset${impactedPipelineItems === 1 ? "" : "s"} have task mapping issues. Pullback keeps them visible and repair stays review-driven.`}
        />
      )}

      {repairPlan && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Fix Missing Pipeline Steps</h3>
              <p className="mt-1 text-xs text-slate-600">
                {repairPlan.repairableCount} task{repairPlan.repairableCount === 1 ? "" : "s"} can be repaired automatically. {repairPlan.reviewCount} still need review.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setRepairPlan(null)}
                className="rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => void handleApplyRepair()}
                disabled={repairPlan.repairableCount === 0 || isApplyingRepair}
                className="rounded-xl border border-slate-200 bg-slate-900 px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isApplyingRepair ? "Applying..." : "Apply Safe Repairs"}
              </button>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
              <h4 className="text-sm font-semibold text-emerald-900">Repairable Automatically</h4>
              <div className="mt-3 space-y-3">
                {repairPlan.repairable.length === 0 ? (
                  <p className="text-xs text-emerald-900/80">No automatic pipeline-step fixes are currently available.</p>
                ) : (
                  repairPlan.repairable.map((item) => (
                    <div key={item.fptTaskId} className="rounded-xl border border-emerald-200 bg-white p-3 text-xs">
                      <div className="font-semibold text-slate-900">{item.taskName}</div>
                      <div className="mt-1 text-slate-600">{item.parentEntityName} • {item.parentEntityType}</div>
                      <div className="mt-2 text-emerald-900">
                        {item.resolvedPipelineStepName} • {item.resolvedDepartmentName}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <h4 className="text-sm font-semibold text-amber-900">Needs Review</h4>
              <div className="mt-3 space-y-3">
                {repairPlan.needsReview.length === 0 ? (
                  <p className="text-xs text-amber-900/80">Nothing is blocked for manual review right now.</p>
                ) : (
                  repairPlan.needsReview.map((item) => (
                    <div key={item.fptTaskId} className="rounded-xl border border-amber-200 bg-white p-3 text-xs">
                      <div className="font-semibold text-slate-900">{item.taskName}</div>
                      <div className="mt-1 text-slate-600">{item.parentEntityName} • {item.parentEntityType}</div>
                      <div className="mt-2 text-amber-900">{item.reason || "Missing or ambiguous mapping."}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-3.5">
          <h3 className="text-base font-semibold text-slate-900">Bid Scope Items</h3>
          <p className="mt-1 text-xs text-slate-600">
            Rows show the current shot and asset bid scope, with pulled FPT production state layered in where linked.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {[
              { key: "all" as const, label: `All (${itemRows.length})` },
              { key: "invoice_ready" as const, label: `Invoice Ready (${invoiceReadyCount})` },
              { key: "blocked" as const, label: `Blocked (${itemRows.length - invoiceReadyCount})` },
              { key: "profitable" as const, label: `Profitable (${itemRows.filter((item) => item.profitabilityStatus === "profitable").length})` },
              { key: "loss_making" as const, label: `Loss-making (${itemRows.filter((item) => item.profitabilityStatus === "loss_making").length})` },
              {
                key: "low_confidence" as const,
                label: `Low Confidence (${itemRows.filter((item) => item.profitabilityReliabilityLevel === "low" || item.profitabilityStatus === "insufficient_data").length})`,
              },
            ].map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setItemFilter(option.key)}
                className={cn(
                  "rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] transition",
                  itemFilter === option.key
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {visibleItemRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center">
            <AlertTriangle className="h-10 w-10 text-slate-300" />
            <div>
              <p className="text-sm font-semibold text-slate-900">
                {itemFilter === "all" ? "No shots or assets are available yet" : "No items match this ShotGrid filter"}
              </p>
              <p className="mt-2 text-xs text-slate-600">
                {itemFilter === "all"
                  ? "Once the bid contains shot or asset scope, this list will show the exact items we plan to sync to FPT."
                  : "Try switching back to All, or pull the latest FPT state to refresh profitability and invoice readiness."}
              </p>
            </div>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-[100px_minmax(0,1.5fr)_110px_140px_120px_140px_140px] gap-3 border-b border-slate-200 bg-slate-50 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              <div>Type</div>
              <div>Name</div>
              <div>Bid Status</div>
              <div>FPT Status</div>
              <div>Sync</div>
              <div>Pipeline</div>
              <div>Invoice</div>
            </div>
            <div className="divide-y divide-slate-100">
              {[
                { title: "Shots", groups: bidScopeCollections.shotGroups },
                { title: "Assets", groups: bidScopeCollections.assetGroups },
              ].map((section) =>
                section.groups.length > 0 ? (
                  <div key={section.title}>
                    <div className="border-b border-slate-100 bg-white px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                      {section.title}
                    </div>
                    {section.groups.map((group) => {
                      const isCollapsed = collapsedBidScopeGroups[group.key] === true;
                      return (
                        <div key={group.key} className="border-b border-slate-100 last:border-b-0">
                          <button
                            type="button"
                            onClick={() => toggleBidScopeGroup(group.key)}
                            className="flex w-full items-center justify-between gap-3 bg-slate-50/70 px-4 py-2.5 text-left transition hover:bg-slate-50"
                          >
                            <div className="flex items-center gap-2">
                              {isCollapsed ? <ChevronRight className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-600">{group.label}</span>
                            </div>
                            <span className="text-xs text-slate-500">{group.rows.length} item{group.rows.length === 1 ? "" : "s"}</span>
                          </button>
                          {!isCollapsed &&
                            group.rows.map((item) => (
                              <div
                                key={item.id}
                                className="grid grid-cols-[100px_minmax(0,1.5fr)_110px_140px_120px_140px_140px] gap-3 border-t border-slate-100 px-4 py-2.5 text-xs first:border-t-0"
                              >
                                <div className="flex items-center">
                                  <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-600">
                                    {item.itemType}
                                  </span>
                                </div>
                                <div className="min-w-0">
                                  <div className="truncate text-sm font-semibold text-slate-900">{item.name}</div>
                                  <div className="mt-1 text-xs text-slate-500">{item.lastContext}</div>
                                  {item.financialContext && <div className="mt-1 text-xs text-slate-600">{item.financialContext}</div>}
                                  {item.profitabilityContext && (
                                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                                      <span>{renderProfitabilityContextWithHighlightedBid(item.profitabilityContext)}</span>
                                      <ProfitabilityBadge status={item.profitabilityStatus} />
                                    </div>
                                  )}
                                  {item.warning && <div className="mt-1 text-xs text-amber-700">{item.warning}</div>}
                                </div>
                                <div className="flex items-center text-slate-700">{item.bidStatus}</div>
                                <div className="flex items-center text-slate-700">{item.fptStatusLabel}</div>
                                <div className="flex items-center">
                                  <StatusBadge status={item.syncStatus} />
                                </div>
                                <div className="flex items-center">
                                  <StatusBadge status={item.pipelineStatus} />
                                </div>
                                <div className="flex items-center" title={item.invoiceStatusNote}>
                                  <StatusBadge status={item.invoiceStatus} />
                                </div>
                              </div>
                            ))}
                        </div>
                      );
                    })}
                  </div>
                ) : null
              )}
            </div>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3.5 text-xs text-slate-600">
        <div className="flex items-start gap-3">
          <BadgeCheck className="mt-0.5 h-4 w-4 text-emerald-600" />
          <p>
            Pullback and repair stay scoped to the ShotGrid tab only. Nothing here deletes or recreates FPT shots, assets, or
            tasks, and app-owned bid/commercial data is left untouched.
          </p>
        </div>
      </div>
    </div>
  );
}
